// Mirrors backend CertificationStatus enum names. The API serializes
// statuses as `enum.name` (UPPERCASE) — see backend/support/serializers.py.
// Keep this in sync when the ORM enum changes.
//
// All seven members of orm.CertificationStatus, in the ORM's declaration
// order. UNCERTIFIED is NOT an enum member — it's the synthetic bucket for a
// work whose latest certification is NULL, which the works route accepts as
// the literal `uncertified` (see _apply_filters).
export const CERTIFICATION_STATUSES = [
  "PENDING",
  "IN_PROCESS",
  "APPROVED",
  "NEEDS_REVIEW",
  "REJECTED",
  "REVOKED",
  "RETRACTED",
  "UNCERTIFIED",
] as const;

// The stats endpoint keys its by_status map on the enum name, except for the
// statusless bucket: Python's json.dumps coerces a None dict key to the string
// "null". Filtering by it uses the synthetic UNCERTIFIED label instead.
export const UNCERTIFIED_STATS_KEY = "null";

export type CertificationStatus = (typeof CERTIFICATION_STATUSES)[number];

// Tolerates either case so legacy data / mixed-case strings still color
// correctly. The current API contract is UPPER but this stays defensive.
//
// Also normalizes space to underscore: the API sends the enum NAME
// ("NEEDS_REVIEW") but the ORM's enum VALUE is "needs review", and either
// spelling has a way of turning up in exported or hand-entered data.
//
// The palette encodes what a support reviewer needs to triage at a glance:
// orange is the only "someone must act" state, and the two post-approval
// reversals share a colour distinct from a plain rejection.
export function statusColor(status: string | null): string {
  switch ((status ?? "").toLowerCase().replace(/ /g, "_")) {
    case "approved":
      return "green";
    case "in_process":
      // Transient: the pipeline holds it, nobody needs to act.
      return "blue";
    case "pending":
      return "yellow";
    case "needs_review":
      // The queue this app exists to drain — must stand out from PENDING.
      return "orange";
    case "rejected":
      return "red";
    case "revoked":
    case "retracted":
      // Was approved, no longer is (by HII / by the creator respectively).
      // One colour for "reversed"; the badge text carries the distinction.
      return "purple";
    default:
      return "gray";
  }
}
